/**
 * Edit form for a field event summary.
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { Textarea } from '@/components/forms/Textarea';
import { getEventSummary, updateEventSummary } from '@/services/event-summaries.service';
import { EVENT_SUMMARY_FORM_QUERY_KEYS } from './event-summary-configs';
import { buildUpdateEventSummaryDto, initialEventSummaryContent } from './event-summary-form-utils';

/** Props for {@link EditEventSummaryView}. */
interface EditEventSummaryViewProps {
  summaryId: string;
  onClose: () => void;
}

/** Edit form for a field event summary. */
export const EditEventSummaryView: React.FC<EditEventSummaryViewProps> = ({
  summaryId,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const {
    data: summary,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: EVENT_SUMMARY_FORM_QUERY_KEYS.one(summaryId),
    queryFn: () => getEventSummary(summaryId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'eventSummaryNotFound',
    onClose,
    entityId: summaryId,
  });
  const [content, setContent] = React.useState('');
  const [isHydrated, setIsHydrated] = React.useState(false);

  React.useEffect(() => {
    if (!summary) return;
    setContent(initialEventSummaryContent(summary));
    setIsHydrated(true);
  }, [summary]);

  const updateMutation = useMutation({
    mutationFn: () => updateEventSummary(summaryId, buildUpdateEventSummaryDto(content)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: EVENT_SUMMARY_FORM_QUERY_KEYS.list });
      toast.success('Synthèse mise à jour');
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  let formBody: React.ReactNode = null;
  if (isError) {
    formBody = (
      <p className="text-sm text-destructive">{getErrorMessage('loadEventSummaryFailed')}</p>
    );
  } else if (summary) {
    formBody = (
      <div className="space-y-4">
        <Textarea
          label="Avis"
          name="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={10}
          placeholder="Saisir votre synthèse..."
        />
      </div>
    );
  }

  return (
    <CollapseReplaceForm
      formType="edit"
      title="Modifier la synthèse"
      subtitle="Modifiez les informations de la synthèse"
      onClose={onClose}
      onSubmit={() => updateMutation.mutate()}
      isSubmitting={updateMutation.isPending}
      isValid={!!summary}
      isLoading={isLoading || (!isError && !isHydrated)}
      submitLabel="Enregistrer"
    >
      {formBody}
    </CollapseReplaceForm>
  );
};
